import _ from 'lodash'
import { ApplicationContext } from '../applicationContext'
import levenshtein from 'fast-levenshtein'

export function chooseTrailer(trailers: any, userFacility: { entityId: any }) {
  const getPriority = (trailer: any) => {
    const hasSpot = !!_.get(trailer, 'core_yms_trailer.spot')
    const trailerFacilityId = _.get(trailer, 'core_yms_trailer.facility.entityId')
    const isAtUserFacility = !!userFacility?.entityId && trailerFacilityId === userFacility?.entityId

    if (hasSpot && isAtUserFacility) {
      return 0
    }
    if (isAtUserFacility) {
      return 1
    }
    if (hasSpot) {
      return 2
    }
    return 3
  }

  return _.orderBy(
    trailers,
    [getPriority, 'modifiedDate'],
    ['asc', 'desc']
  )[0]
}

export async function chooseCarrier(this: ApplicationContext, matchEntity: any, firmId: string) {
  const context = this as ApplicationContext
  const store = context?.getStore()

  // Build a normalized search profile from the user-entered legal name. This drives
  // both the Elasticsearch query strings and the local ranking/scoring logic.
  const searchProfile = createCarrierSearchProfile(matchEntity?.business?.legalName || '')
  const strongMatchQuery = buildStrongCarrierQuery(searchProfile, matchEntity, firmId)
  const weakMatchQuery = buildWeakCarrierQuery(searchProfile, firmId)

  const userScac = matchEntity?.carrier?.scac?.trim()
  const userDot = matchEntity?.carrier?.dotNumber?.trim()

  try {
    // Stage 1 — strong/fuzzy search: exact field matches plus Elasticsearch fuzzy name matching.
    // Results are re-ranked locally to bring the best overall match to the top.
    const strongMatchResults = _.map((await store.queryRecords(strongMatchQuery))?.children, (result) => result.data) || []

    const exactMatch = strongMatchResults.find((result) => {
      const candidateScac = result.carrier?.scac?.trim()
      const candidateDot = result.carrier?.dotNumber?.trim()

      // Two carriers with different SCACs are intentionally distinct fleets (e.g. JB Hunt QPMT vs JB Hunt HJBI),
      // so a name match alone is not sufficient. We only flag as a duplicate when:
      //   - at least one side has no SCAC (we can't rule out same fleet), OR
      //   - both SCACs match exactly.
      const sameOrAmbiguousScac = !userScac || !candidateScac || _.toUpper(userScac) === _.toUpper(candidateScac)

      // Same logic for DOT numbers — different DOTs mean different legal entities,
      // but a missing DOT on either side is ambiguous, so we err on the side of caution.
      const sameOrAmbiguousDot = !userDot || !candidateDot || _.toUpper(userDot) === _.toUpper(candidateDot)

      // A SCAC match is always a strong exact signal, regardless of name or DOT.
      const scacMatch = !_.isEmpty(userScac) && _.toUpper(candidateScac) === _.toUpper(userScac)

      // A DOT match is a strong signal only when the SCACs are ambiguous or match — two records
      // with the same DOT but different SCACs represent distinct fleet entries and are allowed.
      const dotMatch = !_.isEmpty(userDot) && _.toUpper(candidateDot) === _.toUpper(userDot)

      // A name match requires both SCAC and DOT matching/ambiguity to be considered a duplicate.
      const nameMatch = !_.isEmpty(matchEntity?.business?.legalName) &&
        _.toUpper(result.business?.legalName?.trim()) === _.toUpper(matchEntity?.business?.legalName?.trim())

      return scacMatch || (dotMatch && sameOrAmbiguousScac) || (nameMatch && sameOrAmbiguousScac && sameOrAmbiguousDot)
    })

    if (exactMatch) {
      // Definitive duplicate — block creation.
      return { result: exactMatch, confidence: 'strong' }
    } else if (strongMatchResults.length > 0) {
      // Fuzzy name match found but no exact duplicate. Warn the user but allow creation.
      // When the user has provided a SCAC, prefer the candidate whose SCAC is closest
      // to the user's by Levenshtein distance (e.g. "QPML" → "QPMT" before "HJBI").
      const bestFuzzyMatch = userScac
        ? _.minBy(strongMatchResults, (result) => {
            const candidateScac = result.carrier?.scac?.trim()
            return candidateScac ? levenshtein.get(_.toUpper(userScac), _.toUpper(candidateScac)) : Infinity
          }) ?? strongMatchResults[0]
        : strongMatchResults[0]
      return { result: bestFuzzyMatch, confidence: 'medium' }
    }

    // Stage 2 — weak prefix search + local ranking
    // Only run when the user has typed enough to get a meaningful prefix signal;
    // the strong query's fuzzy matching handles very short tokens adequately.
    if (!hasWeakCarrierSearchSignal(searchProfile)) {
      return []
    }

    const weakPrefixMatchResults = _.map((await store.queryRecords(weakMatchQuery))?.children, (result) => result.data) || []
    const weakRankedMatches = rankCarrierMatches(searchProfile, weakPrefixMatchResults)
    if (weakRankedMatches.length > 0) {
      return { result: weakRankedMatches[0], confidence: 'weak' }
    }

    return []
  } catch (e) {
    console.error(`failed to query carrier records: ${e}`)
    return []
  }
}


// ------HELPERS-------

const CARRIER_NAME_PATH = 'business.legalName'
const IGNORE_QUERY_VALUE = '__IGNORE__'

function baseCarrierQuery(firmId: string) {
  const query: any = {
    filters: [
      {
        type: 'containsEdge',
        path: 'mixins.active',
        values: [
          { entityId: '11111111-0000-0000-0000-000000000004' }
        ]
      },
    ],
    orders: [
      { path: 'business.legalName', type: 'ascending' },
      { path: 'carrier.scac', type: 'ascending' },
      { path: 'carrier.dotNumber', type: 'ascending' },
      { path: 'modifiedDate', type: 'descending' },
    ],
    debug: { context: 'existingCarrierLookup' },
    metadata: {
      size: 500,
      shouldIncludeLeafEntities: true,
    }
  }

  if (firmId) {
    // Scope results to the user's firm — carriers from other firms should never be flagged as duplicates.
    query.filters.push({
      type: 'matchFirm',
      value: { uniqueId: firmId },
    })
  }
  return query
}

// Legal entity suffixes stripped from both sides before core-token comparison. This allows
// a suffix-free search like "XYZ" to match candidates with any suffix (or none). When both
// sides do include a recognized suffix, getCarrierMatchScore applies a bonus or penalty via
// LEGAL_SUFFIX_ALIASES instead of treating all suffixes as equivalent.
const LEGAL_SUFFIX_TOKENS = new Set([
  'co',
  'company',
  'corp',
  'corporation',
  'inc',
  'incorporated',
  'llc',
  'llp',
  'lp',
  'ltd',
])

// Maps abbreviated legal-entity suffixes to their canonical long-form equivalents, and
// includes canonical forms mapped to themselves. Used by getTrailingSuffixToken to compare
// trailing suffixes between the search and candidate: when both sides carry a suffix, a
// canonical match boosts the score and a conflict reduces it.
const LEGAL_SUFFIX_ALIASES = new Map<string, string>([
  ['co', 'company'],
  ['company', 'company'],
  ['corp', 'corporation'],
  ['corporation', 'corporation'],
  ['inc', 'incorporated'],
  ['incorporated', 'incorporated'],
  ['ltd', 'limited'],
  ['limited', 'limited'],
  ['llc', 'llc'],
  ['llp', 'llp'],
  ['lp', 'lp'],
])

// Maps common abbreviations and truncations to their canonical long forms before token comparison.
// This ensures "trans" and "transport" are treated as equivalent to "transportation", preventing
// false mismatches when carriers abbreviate freely in their legal names.
const ALIASED_CARRIER_TERM_TOKENS = new Map<string, string>([
  ['logistic', 'logistics'],
  ['trans', 'transportation'],
  ['transport', 'transportation']
])

type CarrierSearchProfile = {
  rawName: string
  normalizedName: string
  strongMatchSearchStrings: string[]
  weakSearchStrings: string[]
}

function buildStrongCarrierQuery(searchProfile: CarrierSearchProfile, matchEntity: any, firmId: string) {
  const strongMatchQuery = _.cloneDeep(baseCarrierQuery(firmId))

  strongMatchQuery.filters.push({
    type: 'or',
    orFilters: [
      [
        {
          type: 'match',
          path: CARRIER_NAME_PATH,
          fuzziness: {
            configType: 'auto',
          },
          values: searchProfile.strongMatchSearchStrings.length > 0
            ? searchProfile.strongMatchSearchStrings
            : [IGNORE_QUERY_VALUE],
        },
      ],
      [
        {
          type: 'match',
          path: 'carrier.scac',
          values: matchEntity?.carrier?.scac ? [matchEntity?.carrier?.scac, _.toUpper(matchEntity?.carrier?.scac)] : [IGNORE_QUERY_VALUE],
        },
      ],
      [
        {
          type: 'match',
          path: 'carrier.dotNumber',
          value: matchEntity?.carrier?.dotNumber || IGNORE_QUERY_VALUE,
        },
      ],
    ],
  })

  return strongMatchQuery
}

function buildWeakCarrierQuery(searchProfile: CarrierSearchProfile, firmId: string) {
  const weakMatchQuery = _.cloneDeep(baseCarrierQuery(firmId))

  weakMatchQuery.filters.push({
    type: 'multipleMatch',
    queryType: 'phrasePrefix',
    values: searchProfile.weakSearchStrings.length > 0
      ? searchProfile.weakSearchStrings
      : [IGNORE_QUERY_VALUE],
    orPaths: [{ path: CARRIER_NAME_PATH }],
  })

  return weakMatchQuery
}

// Removes trailing tokens that appear in `tokensToStrip` from the end of the array.
// Only the trailing end is stripped — a suffix appearing mid-name (e.g. "Inc & Carry") is
// intentionally left in place since it isn't functioning as a legal entity suffix there.
function stripTokens(tokens: string[], tokensToStrip: Set<string>): string[] {
  const strippedTokens = [...tokens]
  while (strippedTokens.length > 0 && tokensToStrip.has(strippedTokens[strippedTokens.length - 1])) {
    strippedTokens.pop()
  }
  return strippedTokens
}

// Returns true when the search profile contains enough text to make a weak prefix query worthwhile.
// Single-character tokens produce too many false positives with phrasePrefix matching — we wait
// for at least 2 characters in the final token. A trailing connector (& or +) signals the user
// has finished one name component and is about to type another, so those are also treated as ready.
// Note: `rawName` is already trimmed by `createCarrierSearchProfile`, so no extra trim needed.
function hasWeakCarrierSearchSignal(searchProfile: CarrierSearchProfile): boolean {
  const tokens = searchProfile.rawName.split(/\s+/).filter(Boolean)
  const lastToken = tokens[tokens.length - 1] || ''
  return lastToken.length >= 2 || /[&+]$/.test(searchProfile.rawName)
}

// Scores all candidate carriers against the user's search profile, discards non-matches (null score),
// and returns candidates ordered by: score DESC → normalized-name Levenshtein ASC → modifiedDate DESC.
// The Levenshtein tiebreaker ensures that when two candidates earn the same score, the one whose
// full normalized name is closer to the search string ranks higher.
function rankCarrierMatches(searchProfile: CarrierSearchProfile, candidateCarriers: any[]) {
  return _.chain(candidateCarriers)
    .map((carrier) => {
      const candidateName = _.get(carrier, 'business.legalName', '')
      const candidateProfile = createCarrierSearchProfile(candidateName)
      const score = getCarrierMatchScore(searchProfile, candidateProfile)
      if (score === null) {
        return null
      }

      return {
        carrier,
        distance: levenshtein.get(searchProfile.normalizedName, candidateProfile.normalizedName),
        score,
      }
    })
    .compact()
    .orderBy(['score', 'distance', (match) => match.carrier.modifiedDate], ['desc', 'asc', 'desc'])
    .map('carrier')
    .uniqBy('uniqueId')
    .value()
}

// Computes a similarity score between the search profile and a candidate carrier profile.
// Returns null if the candidate does not meet the minimum match threshold.
//
// Scoring tiers (highest to lowest confidence):
//   300 — Full normalized-name exact match (case/accent/punctuation insensitive).
//   275 — Core-token match where both sides carry a recognized legal suffix that agrees
//          after aliasing (e.g. "ABC Inc" → "ABC Incorporated").
//   260 — Core-token match with no suffix signal (neither or only one side has a suffix).
//   245 — Core-token match where both sides carry a recognized legal suffix that conflicts
//          (e.g. "ABC Inc" → "ABC Co").
//    45 — Minimum composite score to surface as any weak match. Candidates below this
//          threshold are discarded entirely.
//
// Composite score components:
//   Per-token score  (see getCarrierTokenScore) — main signal
//   +8 per leading exact-match token            — rewards front-loaded precision; "ABC Hunt"
//                                                 should outrank "Hunt ABC" when searching "ABC Hunt"
//   -6 per extra candidate token                — penalizes candidates that are significantly
//                                                 longer than the query (diluted name signal)
//   -4 per extra search token                   — lighter penalty for extra search tokens,
//                                                 since the user may have typed more than stored
//   -Levenshtein(fullName, fullName), capped 12 — final tiebreaker across otherwise similar scores
function getCarrierMatchScore(searchProfile: CarrierSearchProfile, candidateProfile: CarrierSearchProfile): number | null {
  if (searchProfile.normalizedName === candidateProfile.normalizedName) {
    return 300
  }

  const searchTokens = getCoreCarrierTokens(searchProfile.normalizedName)
  const candidateTokens = getCoreCarrierTokens(candidateProfile.normalizedName)

  if (searchTokens.length === 0 || candidateTokens.length === 0) {
    return null
  }

  if (_.isEqual(searchTokens, candidateTokens)) {
    // Core tokens match — suffixes are stripped so both sides share the same trade name.
    // If both sides carry a recognized legal suffix, use it as a tiebreaker: matching
    // suffixes (e.g. "Inc" → "Incorporated") score higher; conflicting suffixes score lower.
    const searchSuffix = getTrailingSuffixToken(searchProfile.normalizedName)
    const candidateSuffix = getTrailingSuffixToken(candidateProfile.normalizedName)
    if (searchSuffix !== null && candidateSuffix !== null) {
      return searchSuffix === candidateSuffix ? 275 : 245
    }
    return 260
  }

  let score = 0
  for (const [index, searchToken] of searchTokens.entries()) {
    const candidateToken = candidateTokens[index]
    if (!candidateToken) {
      // The candidate has fewer tokens than the search — all candidate tokens have been
      // matched. The remaining search tokens are trailing extras (e.g. "lines" in
      // "southeastern freight lines" vs "southeastern freight"). Stop here; the extra
      // search-token penalty applied below handles the score reduction.
      break
    }

    const tokenScore = getCarrierTokenScore(searchToken, candidateToken, index === searchTokens.length - 1)
    if (tokenScore === null) {
      // A single token with no acceptable match disqualifies the entire candidate.
      return null
    }

    score += tokenScore
  }

  // Bonus: reward candidates where the first N tokens match exactly in order.
  const leadingExactMatchCount = _.takeWhile(searchTokens, (token, index) => candidateTokens[index] === token).length
  score += leadingExactMatchCount * 8

  // Penalty: extra tokens on either side reduce signal quality.
  score -= Math.max(candidateTokens.length - searchTokens.length, 0) * 6
  score -= Math.max(searchTokens.length - candidateTokens.length, 0) * 4

  // Tiebreaker: full-name Levenshtein distance (capped at 12 to limit its influence).
  score -= Math.min(levenshtein.get(searchProfile.normalizedName, candidateProfile.normalizedName), 12)

  return score >= 45 ? score : null
}

// Scores a single token pair and returns null if the tokens are too dissimilar to count as a match.
// A null return from any token disqualifies the entire candidate in getCarrierMatchScore.
//
// Score values per branch:
//   40 — Exact character match; maximum token confidence.
//   26 — Candidate starts with the search token (prefix match, only on the final token).
//        Used when the user is mid-typing: "transp" should match "transportation".
//        Requires ≥2 search characters to avoid single-letter wildcards.
//   18–30 — Levenshtein within allowed edit distance; score decreases linearly with distance.
//        Allowed edit distance scales with token length to tolerate proportional typos:
//          ≤2 chars → 0 edits (e.g. "jb" must match "jb" exactly)
//          ≤5 chars → 1 edit  (e.g. "hunt" matches "hunt" or "hunts")
//          ≤10 chars → 2 edits (e.g. "freight" tolerates 2-char differences)
//          11+ chars → 3 edits
//   22 — Search token starts with the candidate token (reverse prefix, final token only).
//        Handles stored names that are abbreviated vs. what the user typed
//        (e.g. stored "transport" when user typed "transportation"). Requires ≥3 candidate
//        characters and Levenshtein ≤1 to avoid overly loose matches.
//  null — No acceptable match; disqualifies the candidate entirely.
function getCarrierTokenScore(searchToken: string, candidateToken: string, isFinalToken: boolean): number | null {
  if (searchToken === candidateToken) {
    return 40
  }

  // Forward prefix: user is still typing the last word.
  if (isFinalToken && candidateToken.startsWith(searchToken) && searchToken.length >= 2) {
    return 26
  }

  const distance = levenshtein.get(searchToken, candidateToken)
  const shortestLength = Math.min(searchToken.length, candidateToken.length)
  const allowedDistance = shortestLength <= 2 ? 0 : shortestLength <= 5 ? 1 : shortestLength <= 10 ? 2 : 3
  if (distance <= allowedDistance) {
    // Score drops by 8 per edit so that exact matches (d=0 → 30) are always preferred
    // over fuzzy matches (d=1 → 22, d=2 → 14 → clamped to 18).
    return Math.max(18, 30 - distance * 8)
  }

  // Reverse prefix: stored name is a shorter form of what the user typed.
  if (isFinalToken && searchToken.startsWith(candidateToken) && candidateToken.length >= 3 && distance <= 1) {
    return 22
  }

  return null
}

// Returns the canonical form of the trailing legal-entity suffix in a normalized name,
// or null if the last token is not a recognized suffix.
function getTrailingSuffixToken(normalizedName: string): string | null {
  const tokens = normalizedName.trim().split(/\s+/).filter(Boolean)
  const last = tokens[tokens.length - 1]
  return (last && LEGAL_SUFFIX_ALIASES.get(last)) ?? null
}

// Splits a normalized name into its meaningful identity tokens by:
//   1. Expanding aliases (trans → transportation, logistic → logistics)
//   2. Stripping trailing legal-entity suffixes (inc, llc, corp, etc.)
// Suffixes are stripped so that suffix-free searches match any suffix variant. Suffix-aware
// ranking is handled separately in getCarrierMatchScore via LEGAL_SUFFIX_ALIASES.
function getCoreCarrierTokens(name: string): string[] {
  return _.chain(name.trim().split(/\s+/).filter(Boolean))
    .map((token) => ALIASED_CARRIER_TERM_TOKENS.get(token) || token)
    .thru((tokens) => stripTokens(tokens, LEGAL_SUFFIX_TOKENS))
    .value()
}

// Normalizes a raw carrier name into a stable, comparison-safe string and generates the
// query strings used by both the strong and weak Elasticsearch filters.
//
// Normalization pipeline (applied in order):
//   1. Replace & and + with " and " — connectors must resolve to the same token regardless
//      of how the carrier stylized their name ("Smith & Sons" vs "Smith and Sons").
//   2. _.lowerCase — lowercases and splits on punctuation/camelCase boundaries (lodash util).
//   3. Unicode NFKD normalization — decomposes accented characters into base + combining marks.
//   4. Strip combining diacritics [\u0300–\u036F] — removes the accent marks left by NFKD,
//      turning "é" into "e", "ñ" into "n", etc.
//   5. Strip apostrophes ['''] — "O'Brien" → "OBrien" (handled as one token, not two).
//   6. Replace slashes with spaces — "A/B Freight" → "A B Freight".
//   7. Strip remaining non-alphanumeric characters — removes stray punctuation.
//   8. Collapse and trim whitespace — normalize any multi-space gaps left by prior steps.
function createCarrierSearchProfile(name: string): CarrierSearchProfile {
  const trimmedName = name.trim()
  const normalizedName = _.lowerCase(
    trimmedName
      .replace(/&/g, ' and ')
      .replace(/\+/g, ' and ')
  )
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, '')
    .replace(/[/\\]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return {
    rawName: trimmedName,
    normalizedName,
    strongMatchSearchStrings: buildStrongMatchSearchStrings(trimmedName),
    weakSearchStrings: buildWeakSearchStrings(trimmedName, normalizedName),
  }
}

function buildStrongMatchSearchStrings(name: string): string[] {
  if (_.isEmpty(name)) {
    return []
  }
  return [
    _.toUpper(name),
    _.startCase(name),
    _.toLower(name),
    _.capitalize(name),
  ]
}

// Generates short prefix strings for the weak phrasePrefix query.
// The approach is:
//   1. Start from both the raw input and the normalized form to match however the
//      stored name may be formatted.
//   2. Produce three connector variants per base value (and / & / +) so that the
//      phrasePrefix filter fires correctly regardless of how the stored name renders
//      the connector (e.g. "Smith & Sons" vs "Smith and Sons" vs "Smith + Sons").
//   3. Truncate each variant to its first 3 characters. Sending the full name as a
//      phrasePrefix value would overly restrict results during early typing; 3 characters
//      gives enough signal to retrieve a broad-but-relevant candidate set that local
//      ranking then narrows down.
//   4. Deduplicate — many connector variants produce identical 3-character prefixes.
function buildWeakSearchStrings(rawInput: string, normalizedInput: string): string[] {
  const baseValues = [rawInput, normalizedInput]
  const replaceConnectorVariant = (value: string, connector: 'and' | '&' | '+'): string => {
    return value.replace(/\s*(?:&|\+|\band\b)\s*/gi, ` ${connector} `)
  }

  const connectorVariants = _.flatMap(baseValues, (value) => {
    if (!value) {
      return []
    }

    return [
      replaceConnectorVariant(value, 'and'),
      replaceConnectorVariant(value, '&'),
      replaceConnectorVariant(value, '+'),
      value,
    ]
  })

  return _.chain(connectorVariants)
    .map((value) => value.slice(0, Math.min(3, value.length)))
    .filter((v): v is string => Boolean(v))
    .uniq()
    .value()
}
